// File: frontend/src/pages/McpIntegrationPage.tsx
// Purpose: Documentation page for MCP Server integration

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Alert,
    Button,
    Divider,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent
} from '@mui/material';
import {
    Code as CodeIcon,
    Computer as ComputerIcon,
    Key as KeyIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as CopyIcon,
    CheckCircle as CheckCircleIcon,
    OpenInNew as OpenInNewIcon,
    GitHub as GitHubIcon,
    Settings as SettingsIcon,
    Search as SearchIcon,
    BugReport as BugReportIcon,
    Article as ArticleIcon,
    Security as SecurityIcon,
    Timeline as TimelineIcon,
    FindInPage as FindInPageIcon,
    HelpOutline as HelpOutlineIcon,
    MenuBook as MenuBookIcon,
    SearchOutlined,
    DataObject,
    TrackChanges,
    Shield,
    CloudQueue,
    AutoAwesome,
    Hub as HubIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { cyber } from '../theme';

// Code block component with copy functionality
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'json' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ position: 'relative', my: 2 }}>
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    bgcolor: cyber.bgDefault,
                    border: `1px solid ${alpha(cyber.primary, 0.25)}`,
                    borderRadius: 1,
                    overflow: 'auto'
                }}
            >
                <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'text.secondary',
                            '&:hover': { color: cyber.primaryLight }
                        }}
                    >
                        {copied ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Typography
                    component="pre"
                    sx={{
                        m: 0,
                        fontFamily: cyber.fontMono,
                        fontSize: '0.85rem',
                        color: 'grey.100',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                    }}
                >
                    {code}
                </Typography>
            </Paper>
        </Box>
    );
};

// Tab panel component
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
);

const McpIntegrationPage: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const navigate = useNavigate();

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const vsCodeConfig = `{
  "servers": {
    "ti-mindmap": {
      "type": "http",
      "url": "https://mcp.ti-mindmap-hub.com/mcp",
      "headers": {
        "X-API-Key": "\${input:tiMindmapApiKey}"
      }
    }
  },
  "inputs": [
    {
      "id": "tiMindmapApiKey",
      "type": "promptString",
      "description": "TI Mindmap HUB API Key",
      "password": true
    }
  ]
}`;

    const claudeDesktopConfigWindows = `{
  "mcpServers": {
    "ti-mindmap": {
      "command": "C:/Program Files/nodejs/node.exe",
      "args": ["C:/Users/YourName/mcp-bridge/mcp-bridge.js"],
      "env": {
        "TI_MINDMAP_API_KEY": "your-api-key-here"
      }
    }
  }
}`;

    const claudeDesktopConfigMac = `{
  "mcpServers": {
    "ti-mindmap": {
      "command": "node",
      "args": ["/Users/YourName/mcp-bridge/mcp-bridge.js"],
      "env": {
        "TI_MINDMAP_API_KEY": "your-api-key-here"
      }
    }
  }
}`;

    const toolCategories = [
        {
            category: 'Reports',
            icon: <ArticleIcon />,
            count: 5,
            description: 'Access and filter threat intelligence reports',
            tools: [
                {
                    name: 'list_reports',
                    description: 'List available threat intelligence reports with optional filters. Returns title, summary, source, date, tags, and direct link to each report.',
                    params: ['search (optional)', 'tags (optional)', 'source (optional)', 'time_range (optional)', 'limit (optional)']
                },
                {
                    name: 'get_report_details',
                    description: 'Retrieve complete details of a specific report including title, content, source, publication date, tags, and links to all processed content.',
                    params: ['report_id (required)']
                },
                {
                    name: 'get_report_content',
                    description: 'Retrieve specific content from a report such as AI summary, mindmap, TTPs, IOCs, or STIX bundle.',
                    params: ['report_id (required)', 'content_type (optional): summary, raw, mindmap, ttps_table, ttps_execution, five_whats, stix, iocs']
                },
                {
                    name: 'get_available_sources',
                    description: 'Get the list of all available threat intelligence sources monitored by the platform.',
                    params: []
                },
                {
                    name: 'get_available_tags',
                    description: 'Get the list of all available tags for filtering reports.',
                    params: []
                }
            ]
        },
        {
            category: 'Weekly Briefings',
            icon: <TimelineIcon />,
            count: 3,
            description: 'Access weekly threat intelligence briefings',
            tools: [
                {
                    name: 'get_latest_briefing',
                    description: 'Retrieve the latest Weekly Threat Briefing including threat snapshot, top TTPs, targeted sectors, deep dive analysis, and article feed.',
                    params: []
                },
                {
                    name: 'list_briefings',
                    description: 'List all available Weekly Threat Briefings sorted from most recent.',
                    params: []
                },
                {
                    name: 'get_briefing_by_date',
                    description: 'Retrieve a specific Weekly Threat Briefing by date.',
                    params: ['date (required): YYYY-MM-DD format']
                }
            ]
        },
        {
            category: 'IOC Search',
            icon: <FindInPageIcon />,
            count: 1,
            description: 'Search for Indicators of Compromise',
            tools: [
                {
                    name: 'search_ioc',
                    description: 'Search for an Indicator of Compromise (IOC) across all processed articles. Supports IP addresses, domains, file hashes (MD5/SHA1/SHA256), and URLs.',
                    params: ['ioc_value (required)']
                }
            ]
        },
        {
            category: 'CVE Intelligence',
            icon: <SecurityIcon />,
            count: 5,
            description: 'Search and analyze CVE vulnerabilities',
            tools: [
                {
                    name: 'search_cve',
                    description: 'Search for a specific CVE and get detailed information including description, CVSS score, affected products, related articles, threat context, and real-time EPSS data.',
                    params: ['cve_id (required): e.g., CVE-2024-3400']
                },
                {
                    name: 'search_cves_by_keyword',
                    description: 'Search CVEs by keyword matching description, vendor, or product name.',
                    params: ['query (required): min 2 chars', 'limit (optional): default 20, max 100']
                },
                {
                    name: 'list_cves',
                    description: 'List all CVEs in the database with pagination and filters.',
                    params: ['page (optional)', 'size (optional)', 'severity (optional): CRITICAL, HIGH, MEDIUM, LOW', 'sort_by (optional)', 'sort_order (optional)']
                },
                {
                    name: 'get_cves_by_article',
                    description: 'Get all CVEs mentioned in a specific article with threat context.',
                    params: ['article_id (required)']
                },
                {
                    name: 'get_cve_statistics',
                    description: 'Get aggregated CVE statistics including totals, severity distribution, top vendors, most mentioned CVEs, and exploitation data.',
                    params: []
                }
            ]
        },
        {
            category: 'STIX Bundles',
            icon: <CodeIcon />,
            count: 3,
            description: 'Access structured threat intelligence in STIX 2.1 format',
            tools: [
                {
                    name: 'get_stix_bundle',
                    description: 'Retrieve the complete STIX 2.1 bundle for a specific article. Contains structured threat intelligence objects (Threat Actors, Malware, Attack Patterns, Indicators, Vulnerabilities) and relationships. Compatible with MISP, OpenCTI, and Microsoft Sentinel.',
                    params: ['article_id (required)']
                },
                {
                    name: 'list_stix_bundles',
                    description: 'List all available STIX bundles with pagination. Returns metadata including article ID, title, and generation date.',
                    params: ['limit (optional): default 50, max 100', 'offset (optional)']
                },
                {
                    name: 'get_stix_statistics',
                    description: 'Get aggregated statistics on generated STIX bundles including total count, object type distribution, and temporal trends.',
                    params: []
                }
            ]
        },
        {
            category: 'Statistics & Submissions',
            icon: <SearchIcon />,
            count: 2,
            description: 'Platform statistics and article submission',
            tools: [
                {
                    name: 'get_statistics',
                    description: 'Retrieve aggregated platform statistics including total reports, distribution by source, most common tags, and time trends.',
                    params: []
                },
                {
                    name: 'submit_article',
                    description: 'Submit a new article URL for automatic analysis. The platform will extract AI summary, threat mindmap, IOCs, TTPs (MITRE ATT&CK), and generate a STIX 2.1 bundle. Analysis takes a few minutes.',
                    params: ['url (required)']
                }
            ]
        },
        {
            category: 'Knowledge Graph (STIX Constellation)',
            icon: <HubIcon />,
            count: 6,
            description: 'Query the Neo4j-backed knowledge graph of threat entities, relationships, and attack paths',
            tools: [
                {
                    name: 'kg_stats',
                    description: 'Get graph-wide statistics including total reports, canon entities, observed and inferred relationships, top 10 most-referenced entities, and entity type distribution.',
                    params: []
                },
                {
                    name: 'kg_search',
                    description: 'Search CanonEntities by name or alias. Returns matching entities with type, aliases, external IDs, and report count.',
                    params: ['query (required): search term', 'entity_type (optional): filter by entity class (e.g., threat-actor, malware, tool)', 'limit (optional): max results, default 20, max 100']
                },
                {
                    name: 'kg_cluster',
                    description: 'Retrieve the local cluster (ego-graph) around a specific entity. Returns connected nodes and edges with observed and optionally inferred relationships.',
                    params: ['canon_id (required): entity ID', 'depth (optional): traversal depth 1-3, default 1', 'include_inferred (optional): include inferred relationships, default false', 'min_inferred_confidence (optional): min confidence for inferred edges, default 0.7']
                },
                {
                    name: 'kg_timeline',
                    description: 'Get the chronological timeline of report appearances for a specific entity. Shows when and where the entity was mentioned across processed articles.',
                    params: ['canon_id (required): entity ID']
                },
                {
                    name: 'kg_attack_path',
                    description: 'Find attack patterns matching a TTP query and traverse to connected threat actors, malware, tools, and campaigns. Useful for mapping adversary infrastructure.',
                    params: ['ttp_query (required): TTP external_id (e.g., T1566) or name substring', 'depth (optional): traversal depth 1-3, default 2']
                },
                {
                    name: 'kg_cross_report',
                    description: 'Find entities shared between two reports. Useful for identifying common threat actors, malware, or TTPs across different intelligence articles.',
                    params: ['report_id_a (required): first report ID', 'report_id_b (required): second report ID']
                }
            ]
        }
    ];

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                        MCP Server Integration
                    </Typography>
                    <Tooltip title="View full MCP documentation">
                        <IconButton
                            component="a"
                            href="https://docs.ti-mindmap-hub.com/mcp/"
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Connect your AI assistant to TI Mindmap HUB using the Model Context Protocol (MCP).
                    Access threat intelligence reports, CVE data, IOC lookups, STIX bundles, weekly briefings, and knowledge graph queries directly from your IDE or AI chat.
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>What is MCP?</strong> The Model Context Protocol is an open standard that allows AI assistants
                    to securely access external data sources. TI Mindmap HUB's MCP server provides <strong>25 specialized tools</strong> for comprehensive threat intelligence research.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<KeyIcon />}
                        onClick={() => navigate('/profile')}
                    >
                        Generate API Key
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<GitHubIcon />}
                        href="https://github.com/TI-Mindmap-HUB-Org/ti-mindmap-hub-research/tree/main/mcp-integration"
                        target="_blank"
                    >
                        View on GitHub
                    </Button>
                </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Tools Overview Cards */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Tool Categories Overview
                    </Typography>
                    <Tooltip title="MCP Server documentation">
                        <IconButton
                            component="a"
                            href="https://docs.ti-mindmap-hub.com/mcp/server"
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, mt: 2 }}>
                    {toolCategories.map((category) => (
                        <Card key={category.category} variant="outlined">
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    {category.icon}
                                    <Typography variant="h6" component="div">
                                        {category.category}
                                    </Typography>
                                    <Chip
                                        label={category.count}
                                        size="small"
                                        sx={{
                                            bgcolor: alpha(cyber.accent.cyan, 0.12),
                                            border: `1px solid ${alpha(cyber.accent.cyan, 0.4)}`,
                                            color: cyber.accent.cyan,
                                            fontFamily: cyber.fontMono,
                                        }}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>

            {/* Quick Start */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon /> Quick Start
                </Typography>
                
                <List>
                    <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: cyber.accent.green }} /></ListItemIcon>
                        <ListItemText 
                            primary="1. Generate an API Key" 
                            secondary={
                                <>
                                    Go to <Link component="button" onClick={() => navigate('/profile')}>My Profile</Link> and create a new MCP API key
                                </>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: cyber.accent.green }} /></ListItemIcon>
                        <ListItemText 
                            primary="2. Choose your client" 
                            secondary="VS Code with GitHub Copilot or Claude Desktop"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: cyber.accent.green }} /></ListItemIcon>
                        <ListItemText 
                            primary="3. Configure the MCP connection" 
                            secondary="Add the configuration below to your client"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: cyber.accent.green }} /></ListItemIcon>
                        <ListItemText 
                            primary="4. Start asking questions!" 
                            secondary={`"Show me the latest ransomware reports" or "Search for CVE-2024-3400"`}
                        />
                    </ListItem>
                </List>
            </Paper>

            {/* Setup Tabs */}
            <Paper sx={{ mb: 4 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab icon={<CodeIcon />} label="VS Code" iconPosition="start" />
                    <Tab icon={<ComputerIcon />} label="Claude Desktop" iconPosition="start" />
                </Tabs>

                {/* VS Code Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>VS Code + GitHub Copilot Setup</Typography>
                        
                        <Typography variant="body2" paragraph>
                            VS Code 1.99+ with GitHub Copilot supports MCP natively via HTTP connections.
                        </Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Prerequisites</Typography>
                        <List dense>
                            <ListItem><ListItemText primary="• VS Code 1.99 or later" /></ListItem>
                            <ListItem><ListItemText primary="• GitHub Copilot extension installed and active" /></ListItem>
                            <ListItem><ListItemText primary="• TI Mindmap HUB API Key" /></ListItem>
                        </List>

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Configuration</Typography>
                        <Typography variant="body2" paragraph>
                            Create a file named <code>.vscode/mcp.json</code> in your project folder:
                        </Typography>

                        <CodeBlock code={vsCodeConfig} />

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Usage</Typography>
                        <Typography variant="body2" paragraph>
                            1. Open Copilot Chat (<code>Ctrl+Shift+I</code> / <code>Cmd+Shift+I</code>)<br />
                            2. When prompted, enter your API key<br />
                            3. Click the Tools icon (🔧) to verify <strong>ti-mindmap</strong> is connected<br />
                            4. Start asking questions about threat intelligence!
                        </Typography>

                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <strong>Security tip:</strong> Add <code>.vscode/mcp.json</code> to your <code>.gitignore</code> to avoid committing API keys.
                        </Alert>
                    </Box>
                </TabPanel>

                {/* Claude Desktop Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Claude Desktop Setup</Typography>
                        
                        <Typography variant="body2" paragraph>
                            Claude Desktop requires a local bridge script to connect to remote MCP servers.
                        </Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Prerequisites</Typography>
                        <List dense>
                            <ListItem><ListItemText primary="• Claude Desktop installed" /></ListItem>
                            <ListItem><ListItemText primary="• Node.js 18+ installed" /></ListItem>
                            <ListItem><ListItemText primary="• TI Mindmap HUB API Key" /></ListItem>
                        </List>

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Step 1: Download the Bridge</Typography>
                        <Typography variant="body2" paragraph>
                            Download the MCP bridge script and save it to a permanent location:
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            href="https://github.com/TI-Mindmap-HUB-Org/ti-mindmap-hub-research/raw/main/mcp-integration/mcp-bridge.js"
                            target="_blank"
                            startIcon={<OpenInNewIcon />}
                            sx={{ mb: 2 }}
                        >
                            Download mcp-bridge.js
                        </Button>

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Step 2: Configure Claude Desktop</Typography>
                        <Typography variant="body2" paragraph>
                            Edit the Claude Desktop config file:
                        </Typography>
                        <List dense>
                            <ListItem><ListItemText primary="Windows: %APPDATA%\Claude\claude_desktop_config.json" /></ListItem>
                            <ListItem><ListItemText primary="macOS: ~/Library/Application Support/Claude/claude_desktop_config.json" /></ListItem>
                        </List>

                        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}><strong>Windows configuration:</strong></Typography>
                        <CodeBlock code={claudeDesktopConfigWindows} />

                        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}><strong>macOS / Linux configuration:</strong></Typography>
                        <CodeBlock code={claudeDesktopConfigMac} />

                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Step 3: Restart Claude Desktop</Typography>
                        <Typography variant="body2" paragraph>
                            Close Claude Desktop completely and reopen it. You should see <strong>ti-mindmap</strong> in the "Search and tools" menu.
                        </Typography>

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <strong>Note:</strong> Replace <code>YourName</code> with your actual username and <code>your-api-key-here</code> with your API key from the Profile page.
                        </Alert>
                    </Box>
                </TabPanel>
            </Paper>

            {/* Complete Tools Reference */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SearchIcon /> Complete Tools Reference (25 Tools)
                    </Typography>
                    <Tooltip title="MCP Server documentation">
                        <IconButton
                            component="a"
                            href="https://docs.ti-mindmap-hub.com/mcp/server"
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Detailed documentation for all available MCP tools:
                </Typography>

                {toolCategories.map((category) => (
                    <Accordion key={category.category} defaultExpanded={category.category === 'Reports'}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {category.icon}
                                <Typography fontWeight="medium">
                                    {category.category}
                                </Typography>
                                <Chip
                                    label={category.count}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        color: cyber.accent.cyan,
                                        borderColor: alpha(cyber.accent.cyan, 0.5),
                                        fontFamily: cyber.fontMono,
                                    }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Tool Name</strong></TableCell>
                                            <TableCell><strong>Description</strong></TableCell>
                                            <TableCell><strong>Parameters</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {category.tools.map((tool) => (
                                            <TableRow key={tool.name}>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: cyber.fontMono,
                                                            fontSize: '0.85rem',
                                                            fontWeight: 'bold',
                                                            color: cyber.accent.cyan
                                                        }}
                                                    >
                                                        {tool.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {tool.description}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {tool.params.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {tool.params.map((param, idx) => (
                                                                <Typography 
                                                                    key={idx}
                                                                    variant="body2" 
                                                                    sx={{
                                                                        fontFamily: cyber.fontMono,
                                                                        fontSize: '0.75rem',
                                                                        color: 'text.secondary'
                                                                    }}
                                                                >
                                                                    {param}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            None
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))}

                <Alert severity="success" sx={{ mt: 3 }}>
                    <strong>STIX 2.1 Compatibility:</strong> STIX bundles generated by TI Mindmap HUB are fully compatible with
                    industry-standard platforms including <strong>MISP</strong>, <strong>OpenCTI</strong>, and <strong>Microsoft Sentinel</strong>.
                </Alert>
            </Paper>

            {/* Example Prompts */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon /> Example Prompts
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Try asking your AI assistant:
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Reports & Intelligence</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Show me the latest ransomware reports from the past 7 days"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"List reports tagged with APT and targeted at financial sector"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Get the MITRE ATT&CK techniques from report [id]"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>CVE Research</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Search for CVE-2024-3400 and explain its severity with EPSS score"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"List all critical CVEs discovered this week"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Find CVEs related to Microsoft Exchange"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>IOC & STIX</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Find any reports mentioning the domain evil.example.com"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Get the STIX 2.1 bundle for article [id] for import into Sentinel"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Search for this file hash: a1b2c3d4..."'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Analysis & Submission</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary={"\"What's in the latest weekly threat briefing?\""}
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Analyze this threat article: https://example.com/report"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Show me platform statistics and top trending threats"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Knowledge Graph</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Search the knowledge graph for APT28 and show its connections"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Show the attack path for T1566 (Phishing) and related threat actors"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary='"Find shared entities between these two reports"'
                                    primaryTypographyProps={{ fontFamily: cyber.fontMono, fontSize: '0.85rem' }}
                                />
                            </ListItem>
                        </List>
                    </Box>
                </Box>
            </Paper>

            {/* Key Features */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Key Features
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <SearchOutlined sx={{ color: cyber.accent.cyan }} />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Comprehensive IOC Support
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Search across IP addresses, domains, file hashes (MD5/SHA1/SHA256), and URLs
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <DataObject sx={{ color: cyber.accent.coral }} />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                STIX 2.1 Bundles
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Compatible with MISP, OpenCTI, and Microsoft Sentinel for seamless TIP integration
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TrackChanges color="primary" />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                MITRE ATT&CK Mapping
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Automatic extraction of TTPs mapped to MITRE ATT&CK framework
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Shield sx={{ color: cyber.accent.green }} />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Real-time CVE Intelligence
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            CVSS scores, EPSS exploitation probability, and threat context for vulnerabilities
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CloudQueue sx={{ color: cyber.accent.cyan }} />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Multi-Source Intelligence
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Aggregated threat intelligence from 50+ trusted OSINT sources
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AutoAwesome sx={{ color: cyber.accent.coral }} />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Automatic Analysis
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Submit URLs for AI-powered extraction of IOCs, TTPs, and STIX generation
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <HubIcon color="primary" />
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Knowledge Graph
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Explore entity relationships, attack paths, and cross-report correlations via STIX Constellation
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Troubleshooting */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReportIcon /> Troubleshooting
                </Typography>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Tools not appearing in VS Code</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2">
                            1. Verify <code>.vscode/mcp.json</code> exists and is valid JSON<br />
                            2. Reload VS Code (<code>Developer: Reload Window</code>)<br />
                            3. Check that your API key is correct and active<br />
                            4. Ensure GitHub Copilot extension is enabled and authenticated
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Claude Desktop "Server disconnected" error</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2">
                            1. Verify Node.js is installed: <code>node --version</code> (must be v18+)<br />
                            2. Check the path to <code>mcp-bridge.js</code> is correct and the file exists<br />
                            3. Test the bridge manually: <code>node /path/to/mcp-bridge.js</code><br />
                            4. Use forward slashes (<code>/</code>) in paths, even on Windows<br />
                            5. Check Claude Desktop logs for specific error messages
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Authentication errors (401 Unauthorized)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2">
                            1. Verify your API key is active in <Link component="button" onClick={() => navigate('/profile')}>My Profile</Link><br />
                            2. Check that the key hasn't expired or been revoked<br />
                            3. Ensure there are no extra spaces or line breaks in the API key<br />
                            4. Regenerate the API key if issues persist
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Tool calls returning empty results</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2">
                            1. Verify the search parameters are correctly formatted<br />
                            2. Check that the requested resource (report_id, article_id, CVE) exists<br />
                            3. For time-based searches, ensure the time_range parameter is valid<br />
                            4. Review platform statistics to confirm data availability
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* Footer Links */}
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Need more help? Check the full documentation or report an issue.
                </Typography>
                <Button
                    variant="text"
                    href="https://docs.ti-mindmap-hub.com/mcp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<MenuBookIcon />}
                    sx={{ mr: 2 }}
                >
                    Full Documentation
                </Button>
                <Button
                    variant="text"
                    href="https://github.com/TI-Mindmap-HUB-Org/ti-mindmap-hub-research/issues"
                    target="_blank"
                    startIcon={<BugReportIcon />}
                >
                    Report Issue
                </Button>
            </Box>
        </Box>
    );
};

export default McpIntegrationPage;